import { domain } from "../auth/domain";

/**
 * Normalizes a Tanzanian phone number to the +255 format.
 * @param {string} number - The phone number input by the user.
 * @returns {string} The normalized phone number.
 */
export const normalizePhoneNumber = (number) => {
  const cleaned = number.replace(/\s+/g, ""); // Remove all spaces

  // Case 1: Starts with '0' and is 10 digits long (e.g., 0717553945)
  if (cleaned.startsWith("0") && cleaned.length === 10) {
    return `+255${cleaned.substring(1)}`;
  }

  // Case 2: Starts with '255' and is 12 digits long (e.g., 255717553945)
  if (cleaned.startsWith("255") && cleaned.length === 12) {
    return `+${cleaned}`;
  }

  // Otherwise, return the number as is (assuming it's already correct or invalid)
  return cleaned;
};

/**
 * Logs in a user by sending their credentials to the server.
 * @param {string} phoneNumber - The user's phone number.
 * @param {string} password - The user's password.
 * @returns {Promise<object>} The user's access and refresh tokens.
 * @throws {Error} Throws an error with status and message if the login fails.
 */
export const loginUser = async (phoneNumber, password) => {
  const normalizedPhone = normalizePhoneNumber(phoneNumber);
  const response = await fetch(`${domain}jwt/jwt/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      phone_number: normalizedPhone,
      password,
    }),
  });

  if (!response.ok) {
    throw new Error(response.status === 401 ? "Invalid Credentials" : "An unknown error occurred");
  }

  const data = await response.json();

  // Fire and forget audit log (best effort, do not block login)
  try {
    await fetch(`${domain}audit/login/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone_number: normalizedPhone,
      }),
    });
  } catch (_) {
    // Audit logging failures should not block login
  }

  return data;
};
